import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, X, Camera } from "lucide-react";
import { toast } from "sonner";
import { ReadOnlyNote } from "@/components/app/AppShell";
import { api, inputCls, labelCls, btnPrimary, cardCls, CATEGORIES, COUNTRY_CODES, MAX_SERVICES, POLICY_SUGGESTIONS } from "@/lib/api";

const SUBTABS = ["Info", "Services", "Policies", "Action", "Share", "Billing"];

const POLICY_LABELS = [
  ["deposit", "Deposit policy"],
  ["cancellation", "Cancellation policy"],
  ["late_arrival", "Late arrival policy"],
  ["preparation", "Preparation instructions"],
  ["guest", "Guest policy"],
  ["other", "Other"],
];

const Toggle = ({ checked, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-guinep-deep" : "bg-guinep-line"}`}
  >
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const ServiceModal = ({ open, onClose, onSaved, existing }) => {
  const [form, setForm] = useState({ title: "", price: "", duration: "", consultation_required: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(existing || { title: "", price: "", duration: "", consultation_required: false });
  }, [open, existing]);

  if (!open) return null;

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Service needs a name.");
      return;
    }
    setBusy(true);
    try {
      const body = JSON.stringify({ ...form, title: form.title.trim() });
      if (existing) {
        await api(`/services/${existing.service_id}`, { method: "PUT", body });
        toast.success("Service updated");
      } else {
        await api("/services", { method: "POST", body });
        toast.success("Service added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-5" data-testid="service-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-medium text-guinep-ink">{existing ? "Edit service" : "Add service"}</h3>
          <button onClick={onClose} className="text-guinep-mute hover:text-guinep-ink"><X size={18} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className={labelCls}>Service name</label>
            <input data-testid="service-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Retwist" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className={labelCls}>Starting price</label>
              <input data-testid="service-price-input" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. 85" />
            </div>
            <div className="flex-1">
              <label className={labelCls}>Duration</label>
              <input data-testid="service-duration-input" className={inputCls} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Approx. 2 hours" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-guinep-mute">
            <input type="checkbox" data-testid="service-consult-input" checked={form.consultation_required} onChange={(e) => setForm({ ...form, consultation_required: e.target.checked })} className="h-4 w-4 accent-[#1F3A2B]" />
            Consultation required
          </label>
          <button onClick={save} disabled={busy} data-testid="service-save-button" className={btnPrimary}>
            {existing ? "Save changes" : "Add service"}
          </button>
        </div>
      </div>
    </div>
  );
};

const MyPageTab = ({ business, services, reload, readOnly = false }) => {
  const [sub, setSub] = useState("Info");
  const [form, setForm] = useState({ ...business, policies: { ...business.policies }, policy_labels: { ...(business.policy_labels || {}) }, timezone: business.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "" });
  const [serviceModal, setServiceModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInput = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image must be under 3 MB.");
      return;
    }
    setPhotoBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await fetch("/api/business/photo", { method: "POST", credentials: "include", body: data });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed. Please try again.");
      }
      const { photo } = await res.json();
      setForm((f) => ({ ...f, photo }));
      toast.success("Photo saved");
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPolicy = (k) => (e) => setForm((f) => ({ ...f, policies: { ...f.policies, [k]: e.target.value } }));

  const save = async (toastMsg) => {
    setBusy(true);
    try {
      await api("/business/me", { method: "PUT", body: JSON.stringify(form) });
      toast.success(toastMsg);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteService = async (id) => {
    await api(`/services/${id}`, { method: "DELETE" });
    toast.success("Service removed");
    reload();
  };

  const pageUrl = `${window.location.origin}/${form.slug}`;

  return (
    <div data-testid="mypage-tab">
      <h1 className="font-serif text-3xl font-medium tracking-tight text-guinep-ink">My Page</h1>
      <div className="mt-4 flex rounded-full bg-guinep-soft p-1" data-testid="mypage-subtabs">
        {SUBTABS.map((s) => (
          <button
            key={s}
            data-testid={`subtab-${s.toLowerCase()}`}
            onClick={() => setSub(s)}
            className={`flex-1 rounded-full py-1.5 text-[12px] font-semibold transition-colors ${sub === s ? "bg-white text-guinep-deep shadow-sm" : "text-guinep-mute hover:text-guinep-ink"}`}
          >
            {s}
          </button>
        ))}
      </div>
      {readOnly && <ReadOnlyNote />}
      <fieldset disabled={readOnly} className="contents">

      {sub === "Info" && (
        <div className="mt-6 space-y-5" data-testid="info-panel">
          <div>
            <label className={labelCls}>Business photo / logo</label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-guinep-soft font-serif text-xl font-medium text-guinep-deep" data-testid="photo-preview">
                {form.photo ? (
                  <img src={`/api/files/${form.photo}`} alt="Business logo" className="h-full w-full object-cover" />
                ) : (
                  form.name?.charAt(0) || "?"
                )}
              </div>
              <div>
                <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="photo-input" onChange={uploadPhoto} />
                <button
                  data-testid="upload-photo-button"
                  onClick={() => photoInput.current?.click()}
                  disabled={photoBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-guinep-line bg-white px-4 py-2.5 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft disabled:opacity-50"
                >
                  <Camera size={15} /> {photoBusy ? "Uploading…" : form.photo ? "Change photo" : "Upload photo"}
                </button>
                {!form.photo && <p className="mt-1.5 text-xs text-guinep-mute/70">No photo — your business initial shows instead.</p>}
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Business name</label>
            <input data-testid="info-name" className={inputCls} value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className={labelCls}>Owner first name</label>
            <input data-testid="info-owner-name" className={inputCls} value={form.owner_first_name} onChange={set("owner_first_name")} />
          </div>
          <div>
            <label className={labelCls}>Business category</label>
            <select data-testid="info-category" className={inputCls} value={form.category} onChange={set("category")}>
              <option value="">Select…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Business specialty <span className="font-normal text-guinep-mute/60">(optional)</span></label>
            <input data-testid="info-specialty" className={inputCls} value={form.specialty || ""} onChange={set("specialty")} placeholder="Loctician, Nail Technician, Caterer, Photographer, Tutor" />
            <p className="mt-1.5 text-xs text-guinep-mute/70">Tell us what you do.</p>
          </div>
          <div>
            <label className={labelCls}>Short description</label>
            <textarea data-testid="info-description" className={`${inputCls} min-h-[70px] resize-none`} value={form.description} onChange={set("description")} placeholder="What you do, in one or two lines" />
          </div>
          <div>
            <label className={labelCls}>Location or service area</label>
            <input data-testid="info-location" className={inputCls} value={form.location} onChange={set("location")} />
          </div>
          <div>
            <label className={labelCls}>WhatsApp number</label>
            <div className="flex gap-2.5">
              <select data-testid="info-wa-cc" className={`${inputCls} w-24`} value={form.whatsapp_country_code} onChange={set("whatsapp_country_code")}>
                {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="info-wa-number" className={inputCls} value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder="2685550142" />
            </div>
            <p className="mt-1.5 text-xs text-guinep-mute/70">Use the number where you want client enquiries to arrive.</p>
          </div>
          <div>
            <label className={labelCls}>Public page link</label>
            <div className="flex items-center overflow-hidden rounded-xl border border-guinep-line bg-white">
              <span className="border-r border-guinep-line bg-guinep-soft/60 px-3 py-3 text-xs text-guinep-mute">{window.location.host}/</span>
              <input data-testid="info-slug" className="flex-1 px-3 py-3 text-sm text-guinep-ink outline-none" value={form.slug} onChange={set("slug")} />
            </div>
          </div>
          <div className={`${cardCls} divide-y divide-guinep-line/60`}>
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="text-sm font-medium text-guinep-ink">Page is live</span>
              <Toggle testid="toggle-live" checked={form.is_live} onChange={(v) => setForm((f) => ({ ...f, is_live: v }))} />
            </div>
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="text-sm font-medium text-guinep-ink">Taking appointments</span>
              <Toggle testid="toggle-appointments" checked={form.taking_appointments} onChange={(v) => setForm((f) => ({ ...f, taking_appointments: v }))} />
            </div>
          </div>
          <button data-testid="save-business-info" onClick={() => save("Business info saved")} disabled={busy || readOnly} className={btnPrimary}>Save business info</button>
        </div>
      )}

      {sub === "Services" && (
        <div className="mt-6" data-testid="services-panel">
          {services.length < MAX_SERVICES ? (
            <button data-testid="add-service-button" onClick={() => setServiceModal({})} className="w-full rounded-xl border border-dashed border-guinep-line py-3.5 text-sm font-medium text-guinep-mute transition-colors hover:border-guinep-mid hover:text-guinep-ink">
              + Add service
            </button>
          ) : (
            <p className="py-3 text-center text-xs text-guinep-mute" data-testid="services-limit-note">You've reached the 12-service maximum.</p>
          )}
          <div className="mt-4 space-y-3">
            {services.map((s) => (
              <div key={s.service_id} className={`${cardCls} flex items-center justify-between p-4`} data-testid="service-card">
                <div>
                  <p className="text-sm font-semibold text-guinep-ink">{s.title}</p>
                  <p className="mt-0.5 text-xs text-guinep-mute">
                    {s.price && <>From {/^\d/.test(s.price) ? `$${s.price}` : s.price}</>}
                    {s.price && s.duration && " · "}{s.duration}
                    {s.consultation_required && " · Consultation required"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-service-${s.service_id}`} onClick={() => setServiceModal(s)} className="p-2 text-guinep-mute transition-colors hover:text-guinep-ink"><Pencil size={15} /></button>
                  <button data-testid={`delete-service-${s.service_id}`} onClick={() => deleteService(s.service_id)} className="p-2 text-guinep-mute transition-colors hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {services.length === 0 && <p className="py-6 text-center text-sm text-guinep-mute">No services yet.</p>}
          </div>
          <ServiceModal open={serviceModal !== null} existing={serviceModal?.service_id ? serviceModal : null} onClose={() => setServiceModal(null)} onSaved={reload} />
        </div>
      )}

      {sub === "Policies" && (
        <div className="mt-6 space-y-5" data-testid="policies-panel">
          <div>
            <h2 className="font-serif text-lg font-medium text-guinep-ink">Policies & client information</h2>
            <p className="mt-1 text-[13px] text-guinep-mute">Suggested for {form.category || "your business type"} — edit the wording or leave any blank.</p>
          </div>
          {(form.category === "Other Service Business"
            ? [{ key: "custom_1" }, { key: "custom_2" }, { key: "custom_3" }]
            : [...(POLICY_SUGGESTIONS[form.category] || []).map(([key, label]) => ({ key, label })), { key: "other", label: "Anything else" }]
          ).map((p) => (
            <div key={p.key}>
              {p.label ? (
                <label className={labelCls}>{p.label}</label>
              ) : (
                <input
                  data-testid={`policy-label-${p.key}`}
                  className={`${inputCls} mb-2`}
                  value={form.policy_labels[p.key] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, policy_labels: { ...f.policy_labels, [p.key]: e.target.value } }))}
                  placeholder="Policy name (e.g. Booking information)"
                />
              )}
              <textarea
                data-testid={`policy-${p.key}`}
                className={`${inputCls} min-h-[64px] resize-none`}
                value={form.policies[p.key] || ""}
                onChange={(e) => setForm((f) => ({ ...f, policies: { ...f.policies, [p.key]: e.target.value } }))}
                placeholder="Add your wording…"
              />
            </div>
          ))}
          <button data-testid="save-policies" onClick={() => save("Policies saved")} disabled={busy || readOnly} className={btnPrimary}>Save policies</button>
        </div>
      )}

      {sub === "Action" && (
        <div className="mt-6" data-testid="action-panel">
          <p className="text-sm text-guinep-mute">Choose the main action clients see on your page.</p>
          <div className="mt-4 space-y-2.5">
            {[
              ["request_appointment", "Request Appointment", true],
              ["ask_about_service", "Ask About a Service", false],
              ["external_link", "Book Through External Link", false],
              ["message_on_whatsapp", "Message on WhatsApp", false],
            ].map(([value, label, isDefault]) => (
              <button
                key={value}
                data-testid={`action-${value}`}
                onClick={() => setForm((f) => ({ ...f, action_type: value }))}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-sm font-medium transition-colors ${
                  form.action_type === value ? "border-guinep-deep bg-guinep-soft text-guinep-deep" : "border-guinep-line bg-white text-guinep-ink hover:border-guinep-mid/50"
                }`}
              >
                {label}
                {isDefault && <span className="text-[10px] font-semibold uppercase tracking-wider text-guinep-mute/60">Default</span>}
              </button>
            ))}
          </div>
          {form.action_type === "external_link" && (
            <div className="mt-4">
              <label className={labelCls}>External booking link</label>
              <input data-testid="action-external-link" className={inputCls} value={form.external_link} onChange={set("external_link")} placeholder="https://…" />
            </div>
          )}
          <button data-testid="save-action" onClick={() => save("Action saved")} disabled={busy || readOnly} className={`${btnPrimary} mt-6`}>Save action</button>
        </div>
      )}

      {sub === "Share" && <SharePanel form={form} set={set} pageUrl={pageUrl} onSave={() => save("Profiles saved")} busy={busy} readOnly={readOnly} />}

      {sub === "Billing" && <BillingPanel readOnly={readOnly} />}
      </fieldset>
    </div>
  );
};

const STATUS_LABELS = {
  trial: "Free trial", active: "Active", past_due: "Past due",
  cancelled: "Cancelled", expired: "Trial expired", suspended: "Suspended",
};

const fmtDateLong = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—");

const BillingPanel = ({ readOnly = false }) => {
  const [billing, setBilling] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = () => api("/billing/status").then((d) => setBilling(d.billing)).catch(() => {});
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    try {
      await api("/billing/cancel", { method: "POST" });
      toast.success("Subscription cancelled — access continues until the end of your paid period.");
      setConfirming(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!billing) return null;

  const rows = [
    ["Status", STATUS_LABELS[billing.status] || billing.status],
    billing.status === "trial" && ["Trial days remaining", `${billing.trial_days_remaining} days`],
    billing.status === "trial" && ["Trial ends", fmtDateLong(billing.trial_end)],
    ["Plan", `$${billing.monthly_price}/month`],
    billing.status === "trial" && ["First month after trial", `$${billing.first_month_price}`],
    billing.next_billing_date && billing.next_amount && ["Next billing", `${fmtDateLong(billing.next_billing_date)} — $${billing.next_amount}`],
    billing.status === "cancelled" && billing.current_period_end && ["Access until", fmtDateLong(billing.current_period_end)],
  ].filter(Boolean);

  return (
    <div className="mt-6" data-testid="billing-panel">
      <div className={`${cardCls} divide-y divide-guinep-line/60`}>
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-4 py-3.5">
            <span className="text-sm text-guinep-mute">{k}</span>
            <span className="text-sm font-semibold text-guinep-ink">{v}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-guinep-mute">14 days free, then $9 for your first month, $12/month after that. No commission. No transaction fees. Cancel anytime.</p>

      {!readOnly && (
        <Link to="/subscribe" data-testid="billing-manage-link" className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-guinep-deep py-3.5 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid">
          {billing.status === "active" ? "Manage subscription" : "Subscribe"}
        </Link>
      )}

      {!readOnly && billing.status === "active" && !billing.cancel_at_period_end && (
        <div className="mt-4">
          {confirming ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="billing-cancel-confirm">
              <p className="text-[13px] leading-relaxed text-red-800">
                Cancel your subscription? Your page stays live until {fmtDateLong(billing.current_period_end)}, then becomes inactive. Your account and business information are kept, and you can resubscribe anytime.
              </p>
              <div className="mt-3 flex gap-2.5">
                <button onClick={() => setConfirming(false)} className="flex-1 rounded-lg border border-guinep-line bg-white py-2.5 text-xs font-semibold text-guinep-ink">Keep subscription</button>
                <button onClick={cancel} data-testid="billing-cancel-confirm-button" className="flex-1 rounded-lg bg-red-600 py-2.5 text-xs font-semibold text-white">Cancel subscription</button>
              </div>
            </div>
          ) : (
            <button data-testid="billing-cancel-button" onClick={() => setConfirming(true)} className="w-full py-2 text-center text-xs font-medium text-guinep-mute underline-offset-4 transition-colors hover:text-red-600 hover:underline">
              Cancel subscription
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const SharePanel = ({ form, set, pageUrl, onSave, busy, readOnly = false }) => {
  const [qr, setQr] = useState("");

  useEffect(() => {
    import("qrcode").then((QRCode) => {
      QRCode.toDataURL(pageUrl, { margin: 1, width: 220, color: { dark: "#1F3A2B" } }).then(setQr);
    });
  }, [pageUrl]);

  const copyLink = async (note) => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success(note || "Link copied");
    } catch (e) {
      toast.error("Could not copy — long-press the link instead.");
    }
  };

  const shareBtn = "inline-flex items-center justify-center gap-2 rounded-xl border border-guinep-line bg-white py-3 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft";

  return (
    <div className="mt-6" data-testid="share-panel">
      <h2 className="font-serif text-lg font-medium text-guinep-ink">Share your Guinep Seed page</h2>
      <p className="mt-1 text-[13px] text-guinep-mute">Social media creates discovery — Guinep Seed handles what happens after the click.</p>

      <div className={`${cardCls} mt-4 p-4`}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-guinep-mute/60">Your page link</p>
        <p className="mt-1 break-all text-sm font-medium text-guinep-deep" data-testid="share-page-link">{pageUrl}</p>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <button data-testid="share-copy" onClick={() => copyLink()} className={shareBtn}>Copy Link</button>
        <a data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className={shareBtn}>WhatsApp</a>
        <button data-testid="share-instagram" onClick={() => copyLink("Link copied — paste it in your Instagram bio")} className={shareBtn}>Instagram</button>
        <a data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className={shareBtn}>Facebook</a>
        <button data-testid="share-tiktok" onClick={() => copyLink("Link copied — paste it in your TikTok bio")} className={`${shareBtn} col-span-2`}>TikTok</button>
      </div>

      {qr && (
        <div className={`${cardCls} mt-4 flex flex-col items-center p-6`} data-testid="share-qr">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-guinep-mute/60">QR code</p>
          <img src={qr} alt="QR code linking to your page" className="h-44 w-44" />
        </div>
      )}

      <div className="mt-7 space-y-4">
        <p className="text-sm font-semibold text-guinep-ink">Your social profiles</p>
        <div>
          <label className={labelCls}>Instagram username</label>
          <input data-testid="share-ig-input" className={inputCls} value={form.instagram} onChange={set("instagram")} placeholder="crownedroots" />
        </div>
        <div>
          <label className={labelCls}>TikTok username</label>
          <input data-testid="share-tiktok-input" className={inputCls} value={form.tiktok} onChange={set("tiktok")} placeholder="crownedroots" />
        </div>
        <div>
          <label className={labelCls}>Facebook page URL</label>
          <input data-testid="share-fb-input" className={inputCls} value={form.facebook} onChange={set("facebook")} placeholder="https://facebook.com/…" />
        </div>
        <button data-testid="save-profiles" onClick={onSave} disabled={busy || readOnly} className={btnPrimary}>Save profiles</button>
      </div>
    </div>
  );
};

export default MyPageTab;
